import type { Message, PendingMessage } from '@liniya/api-client';
import { parseMessageBody } from '@liniya/api-client';
import { Check, CheckCheck, Clock, Forward, RefreshCw, Reply, TriangleAlert } from 'lucide-react';
import {
  memo,
  useMemo,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { formatTimeOfDay } from '@liniya/client-core';
import { AttachmentView } from '@/features/attachments/AttachmentView';

interface Props {
  message: Message;
  own: boolean;
  channelMode: boolean;
  selected: boolean;
  /** Choosing messages to delete; a bubble becomes a checkbox. */
  selecting: boolean;
  /** False on a message this viewer may not delete, which cannot be chosen. */
  selectable: boolean;
  onToggleSelected: (message: Message) => void;
  onContextMenu: (message: Message, anchor: { x: number; y: number }) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}

/**
 * Delivery state, shown only on the sender's own messages.
 *
 * One tick means the server stored it, two mean a recipient device
 * acknowledged it, two filled mean it was read. Nothing is shown on incoming
 * messages: the reader already knows they received it.
 */
function DeliveryMark({ message }: { message: Message }) {
  if (message.receiptState === 'read') {
    return <CheckCheck size={13} aria-label="Прочитано" style={{ color: 'var(--color-online)' }} />;
  }
  if (message.receiptState === 'delivered') {
    return <CheckCheck size={13} aria-label="Доставлено" />;
  }
  return <Check size={13} aria-label="Отправлено" />;
}

export const MessageBubble = memo(function MessageBubble({
  message,
  own,
  channelMode,
  selected,
  selecting,
  selectable,
  onToggleSelected,
  onContextMenu,
  onToggleReaction,
}: Props) {
  const parsed = useMemo(() => parseMessageBody(message.content), [message.content]);
  const hasMedia = message.attachment_ids.length > 0 && parsed.attachmentName !== null;

  const className = [
    'message-bubble',
    channelMode ? 'channel-post' : own ? 'own' : '',
    hasMedia ? 'has-media' : '',
    selected ? 'is-selected' : '',
    selecting ? 'is-selecting' : '',
    selecting && !selectable ? 'is-unselectable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const openMenu = (x: number, y: number) => onContextMenu(message, { x, y });
  const pick = () => onToggleSelected(message);

  // While choosing what to delete the bubble is a checkbox and nothing else:
  // opening a menu from it would offer Reply and Forward in the middle of a
  // delete, and the pointer is already being used to tick boxes.
  // A message this viewer may not delete is not a disabled checkbox but no
  // control at all: announcing one would promise an action that does not
  // exist, and the menu stays shut here too.
  const selectionProps = selecting
    ? selectable
      ? {
          role: 'checkbox' as const,
          'aria-checked': selected,
          tabIndex: 0,
          onClick: pick,
          onContextMenu: (event: MouseEvent) => {
            event.preventDefault();
            pick();
          },
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key !== ' ' && event.key !== 'Enter') return;
            event.preventDefault();
            pick();
          },
        }
      : { onContextMenu: (event: MouseEvent) => event.preventDefault() }
    : {
        onContextMenu: (event: MouseEvent) => {
          event.preventDefault();
          openMenu(event.clientX, event.clientY);
        },
      };

  return (
    <article className={className} data-message-id={message.message_id} {...selectionProps}>
      {parsed.reference ? (
        <div className="message-reference">
          <strong>
            {parsed.reference.kind === 'reply' ? (
              <>
                <Reply size={12} style={{ display: 'inline' }} /> Ответ на сообщение
              </>
            ) : (
              <>
                <Forward size={12} style={{ display: 'inline' }} /> Пересланное сообщение
              </>
            )}
          </strong>
          <span>{parsed.reference.excerpt}</span>
        </div>
      ) : null}

      {hasMedia && message.attachment_ids[0] ? (
        <AttachmentView
          attachmentId={message.attachment_ids[0]}
          fileName={parsed.attachmentName ?? 'file'}
        />
      ) : null}

      {hasMedia ? (
        parsed.caption ? <p>{parsed.caption}</p> : null
      ) : (
        <p>{parsed.content}</p>
      )}

      <time dateTime={message.created_at}>
        {message.edited_at ? <span className="message-edited">изменено</span> : null}
        {formatTimeOfDay(message.created_at)}
        {own ? <DeliveryMark message={message} /> : null}
      </time>

      {message.reactions.length > 0 ? (
        <div className="message-reactions">
          {message.reactions.map((reaction) => (
            <button
              key={reaction.emoji}
              type="button"
              className={reaction.selected ? 'selected' : ''}
              onClick={() => onToggleReaction(message.message_id, reaction.emoji)}
              aria-pressed={reaction.selected}
              aria-label={`${reaction.emoji}, ${reaction.count}`}
            >
              {reaction.emoji} {reaction.count}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  );
});

/**
 * A message that has not been confirmed yet.
 *
 * It is rendered by a separate component rather than a flag on the real bubble
 * so that the confirmed path stays free of "might not exist yet" branches, and
 * so the two can never be mistaken for one another in the list.
 */
export function PendingBubble({
  message,
  channelMode,
  onRetry,
}: {
  message: PendingMessage;
  channelMode: boolean;
  onRetry: (messageId: string) => void;
}) {
  const failed = message.state === 'failed';
  const className = [
    'message-bubble',
    channelMode ? 'channel-post' : 'own',
    message.attachmentName ? 'has-media' : '',
    failed ? 'is-failed' : 'is-pending',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={className}>
      {message.attachmentName ? (
        <div style={{ position: 'relative' }}>
          <AttachmentView
            attachmentId="pending"
            fileName={message.attachmentName}
            localPreviewUrl={message.localPreviewUrl}
          />
          {!failed && typeof message.uploadProgress === 'number' ? (
            <div className="attachment-progress">
              <div
                className="attachment-progress-circle"
                style={
                  { '--upload-progress': Math.round(message.uploadProgress) } as CSSProperties
                }
              >
                <span>{Math.round(message.uploadProgress)}%</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {message.content ? <p>{message.content}</p> : null}

      <time dateTime={message.createdAt}>
        {formatTimeOfDay(message.createdAt)}
        {failed ? <TriangleAlert size={13} aria-label="Не отправлено" /> : <Clock size={13} aria-label="Отправляется" />}
      </time>

      {failed ? (
        <button
          type="button"
          className="message-retry"
          onClick={() => onRetry(message.message_id)}
        >
          <RefreshCw size={13} /> Повторить
        </button>
      ) : null}
    </article>
  );
}
